'use client';

/**
 * CallTopBarStatus — the mobile DOCKED header (design turn 13's mobile
 * top-bar parity, 13d; reworked into a transparent header by Turn 17's
 * orb-docking pass, 17a). [Turn 17] Only ever rendered POST-DOCK now
 * (call-screen.tsx's pre-dock centered layout has no top bar at all — see
 * that file's Turn 17 note and CallStatusCluster's reuse there) — which
 * mostly means `vizMode`'s four states (idle/user/thinking/agent) drive it
 * in practice, though `dimmed`/`connecting`/`reconnecting`/`statusOverride`
 * all still work exactly as before: a POST-dock mid-call failure (ws_closed
 * reconnecting, a rate-limit EXHAUSTED) renders in THIS docked header, not
 * back in the centered pre-dock layout (see call-screen.tsx's regression
 * note) — so this component keeps its FULL prop surface from 13d/15f/16d,
 * only the bar's own chrome changed.
 *
 * [Turn 17, 17a] The bar's background/border TINT is gone per owner
 * feedback ("the strip look is being removed") — this is now a transparent
 * ~50px row: a 30px orb ~18px from the left edge, the label, and (via
 * `children`) the Live pill on the right. State now reads from the orb's
 * own per-mode ring/glow treatment (call-orb.tsx) plus the label's own
 * per-mode color (T13_STATUS_COLORS) alone — no more colored bar background
 * boosting legibility, so those two channels are the only signal left, and
 * both were already independently state-driven before this pass.
 *
 * Reuses:
 *   - CallOrb directly at size=30 — NOT a copy of its layer stack; 13d's
 *     mandate is that the mini orb carries the same breathe/listen-ring/
 *     shimmer/speak-ripple treatments as the 128px desktop orb, and
 *     call-orb.tsx is written to scale its geometry off the `size` prop for
 *     exactly this reuse (see that file's "Size flexibility" note).
 *   - LiveLevelCue for the green USER bars, at a 2.5px/15px mini geometry
 *     override (13d) instead of its 3px/16-17px desktop default.
 *   - EqualizerBars' 4-bar AGENT palette (EQUALIZER_AGENT_COLORS), same mini
 *     geometry override, for the orange AGENT bars.
 *   - CALL_STATUS_TEXT from call-status-cluster.tsx — one copy source for
 *     both surfaces, so desktop and mobile can never drift on wording.
 *
 * The four glyphs stack via CSS Grid (`col-start-1 row-start-1`, same
 * technique call-status-cluster.tsx uses) rather than `position: absolute`
 * toggling, so the invisible ones still contribute to the grid track's
 * natural (MAX, not SUM) sizing — no manual width bookkeeping needed, and
 * the row layout can't lurch when the active glyph changes.
 *
 * Anti-strobe delays mirror call-status-cluster.tsx exactly: IDLE/THINKING
 * fade IN delayed 400ms (fade out instant), USER (LiveLevelCue) fades IN
 * instantly and fades OUT delayed 400ms (owned internally by that
 * component), AGENT has no delay either direction. Same reasoning as
 * desktop: whichever state represents an ambiguous/blippy signal gets the
 * delay on the side that could otherwise flicker.
 *
 * Turn 15 (15f "mobile parity") adds `dimmed`/`resting`, forwarded straight
 * to CallOrb / the fifth "Resting a moment" glyph — same override rules as
 * their desktop counterparts (call-orb.tsx's `dimmed`, call-status-
 * cluster.tsx's `resting`), so EXHAUSTED looks identical on both surfaces.
 *
 * Turn 16 (16d "mobile parity") adds `connecting`/`reconnecting` (forwarded
 * straight to the mini CallOrb, same as `dimmed`) and mirrors call-status-
 * cluster.tsx's `statusOverrideDots`/`statusOverrideWarm` toggles on the
 * sixth glyph — same override rules as the desktop cluster, just at the
 * bar's mini 4px dot scale (vs desktop's 5px).
 */

import type { ReactNode } from 'react';
import { VizMode } from '@/types/chat';
import { cn } from '@/lib/utils';
import { CallOrb } from './call-orb';
import { LiveLevelCue } from './live-level-cue';
import {
  EqualizerBars,
  EQUALIZER_AGENT_COLORS,
} from '@/components/assistant/primitives/equalizer-bars';
import { CALL_STATUS_TEXT } from './call-status-cluster';
import { T13_STATUS_COLORS } from '@/lib/design-tokens';
import type { AudioLevels } from '@/hooks/useAudioVisualization';

/** Mini-scale mic bars (13d: "2.5px width/15px height") — overrides
 *  LiveLevelCue's 3px/16-17px desktop default. */
const MINI_BAR_HEIGHTS = [11, 9, 13] as const;
/** 4-bar AGENT set at the same mini scale, same color order as the desktop
 *  cluster's EQUALIZER_AGENT_COLORS, shorter to fit the compressed row. */
const MINI_AGENT_HEIGHTS = [8, 13, 10, 15];
const MINI_BAR_WIDTH = 2.5;

export interface CallTopBarStatusProps {
  vizMode: VizMode;
  /** Shared audio level engine ref — feeds the orb and both bar glyphs. */
  levelRef: React.RefObject<AudioLevels>;
  /** The Live pill (call-screen.tsx owns its `showCC`-driven visibility —
   *  rendered here, on the right, exactly where it sat before Turn 13). */
  children?: ReactNode;
  className?: string;
  /** Turn 15 (15f) — true while a rate-limit retry budget is EXHAUSTED;
   *  forwarded to the mini CallOrb and overrides every glyph with "Resting
   *  a moment" (mirrors call-orb.tsx's/call-status-cluster.tsx's props). */
  dimmed?: boolean;
  /** Turn 15 (15f) — bumped once per MODEL_BUSY recovery; forwarded to the
   *  mini CallOrb's release ripple (see call-orb.tsx). */
  releaseSignal?: number;
  /** [FB34] Mirrors call-status-cluster.tsx's `statusOverride` — non-empty
   *  while the call-phase machine is `connecting`/`failed` (see
   *  src/lib/call-phase.ts); wins over every other glyph, `dimmed` included. */
  statusOverride?: string;
  /** [Turn 16] Mirrors call-status-cluster.tsx's `statusOverrideDots`. */
  statusOverrideDots?: boolean;
  /** [Turn 16] Mirrors call-status-cluster.tsx's `statusOverrideWarm`. */
  statusOverrideWarm?: boolean;
  /** [Turn 16] Forwarded straight to the mini CallOrb — see that file's
   *  `connecting` prop. */
  connecting?: boolean;
  /** [Turn 16] Forwarded straight to the mini CallOrb — see that file's
   *  `reconnecting` prop. */
  reconnecting?: boolean;
  /** [Turn 17] Overrides the internally-built 30px CallOrb with a caller-
   *  supplied node — call-screen.tsx injects the SAME `layoutId`-tagged
   *  traveling orb instance it renders pre-dock, so the shared-element FLIP
   *  (17b) has one continuous orb to animate into this header rather than
   *  two independently-mounted CallOrbs crossfading. Omitted → falls back
   *  to building its own CallOrb exactly as before (every pre-Turn-17 call
   *  site/test keeps working unchanged). */
  orb?: ReactNode;
  /** [Turn 17, 17b] "Header status + Live pill fade in" — plays a one-shot
   *  rise on the label grid + `children` (NOT the orb slot, which is
   *  mid-FLIP when this mounts), delayed to the docked-content beat (+200ms
   *  from travel start = this component's mount). Only call-screen.tsx's
   *  docked mobile tree sets it; default off keeps every other call
   *  site/test byte-identical. */
  contentEntrance?: boolean;
}

export function CallTopBarStatus({
  vizMode,
  levelRef,
  children,
  className,
  dimmed = false,
  releaseSignal,
  statusOverride,
  statusOverrideDots = false,
  statusOverrideWarm = false,
  connecting = false,
  reconnecting = false,
  orb,
  contentEntrance = false,
}: CallTopBarStatusProps) {
  const hasOverride = !!statusOverride;
  // [Turn 17, 17b] One-shot entrance for the non-orb content — see the
  // `contentEntrance` prop doc. `both` fill on call-rise holds it hidden
  // through the 200ms delay; motion-reduce strips the animation entirely
  // (content simply present — 17b's reduced-motion "appears without the
  // rise").
  const entranceCn =
    contentEntrance &&
    'motion-safe:animate-call-rise motion-safe:[animation-delay:200ms]';
  const isIdle = !hasOverride && !dimmed && vizMode === VizMode.IDLE;
  const isUser = !hasOverride && !dimmed && vizMode === VizMode.USER;
  const isThinking = !hasOverride && !dimmed && vizMode === VizMode.THINKING;
  const isAgent = !hasOverride && !dimmed && vizMode === VizMode.AGENT;
  const isResting = !hasOverride && dimmed;

  return (
    // [Turn 17, 17a] Transparent ~50px header row — no background, no
    // border (the 13d tinted-strip look is retired per owner feedback); see
    // file header for why state now reads from the orb + label alone.
    <div
      className={cn(
        'flex h-[50px] shrink-0 items-center justify-between pl-[18px] pr-[15px]',
        className
      )}
    >
      <div className="flex min-w-0 items-center gap-[11px]">
        {orb ?? (
          <CallOrb
            size={30}
            mode={vizMode}
            levelRef={levelRef}
            dimmed={dimmed}
            releaseSignal={releaseSignal}
            connecting={connecting}
            reconnecting={reconnecting}
          />
        )}

        <div className={cn('relative grid min-w-0 items-center', entranceCn)}>
          {/* AGENT: orange 4-bar set + label — no anti-strobe delay either
              direction (matches call-status-cluster.tsx). */}
          <div
            className={cn(
              'col-start-1 row-start-1 flex items-center gap-[8px] transition-opacity duration-200',
              isAgent ? 'opacity-[1]' : 'opacity-[0]'
            )}
          >
            <EqualizerBars
              heights={MINI_AGENT_HEIGHTS}
              colors={EQUALIZER_AGENT_COLORS}
              barWidth={MINI_BAR_WIDTH}
              animate={isAgent}
              levelRef={levelRef}
            />
            <span
              className="truncate text-[13px] font-semibold"
              style={{ color: T13_STATUS_COLORS.agentLabel }}
            >
              {CALL_STATUS_TEXT[VizMode.AGENT]}
            </span>
          </div>

          {/* USER: green mini bars (self-fades on `active`, see file header). */}
          <LiveLevelCue
            className="col-start-1 row-start-1"
            levelRef={levelRef}
            active={isUser}
            barMaxHeights={MINI_BAR_HEIGHTS}
            barWidth={MINI_BAR_WIDTH}
          />

          {/* IDLE: dot + "Ready to listen". [Turn 17, 17a] 13px (was 13.5px)
              — the docked header mock's literal label size; applied to
              every glyph in this file for one consistent row, not just
              this one. */}
          <div
            className={cn(
              'col-start-1 row-start-1 flex items-center gap-[8px] transition-opacity duration-200',
              isIdle ? 'opacity-[1] delay-[400ms]' : 'opacity-[0]'
            )}
          >
            <span
              className="size-[6px] shrink-0 rounded-full animate-wave-dot-idle"
              style={{ backgroundColor: T13_STATUS_COLORS.idleDot }}
            />
            <span
              className="truncate text-[13px] font-medium"
              style={{ color: T13_STATUS_COLORS.idleLabel }}
            >
              {CALL_STATUS_TEXT[VizMode.IDLE]}
            </span>
          </div>

          {/* THINKING: three waving dots + "Sara is thinking". */}
          <div
            className={cn(
              'col-start-1 row-start-1 flex items-center gap-[8px] transition-opacity duration-200',
              isThinking ? 'opacity-[1] delay-[400ms]' : 'opacity-[0]'
            )}
          >
            <div
              className="flex shrink-0 items-center gap-[4px]"
              aria-hidden="true"
            >
              <span
                className="size-[5px] rounded-full animate-wave-dot-think"
                style={{ backgroundColor: T13_STATUS_COLORS.thinkingDot }}
              />
              <span
                className="size-[5px] rounded-full animate-wave-dot-think"
                style={{
                  backgroundColor: T13_STATUS_COLORS.thinkingDot,
                  animationDelay: '.25s',
                }}
              />
              <span
                className="size-[5px] rounded-full animate-wave-dot-think"
                style={{
                  backgroundColor: T13_STATUS_COLORS.thinkingDot,
                  animationDelay: '.5s',
                }}
              />
            </div>
            <span
              className="truncate text-[13px] font-semibold"
              style={{ color: T13_STATUS_COLORS.thinkingLabel }}
            >
              {CALL_STATUS_TEXT[VizMode.THINKING]}
            </span>
          </div>

          {/* EXHAUSTED (Turn 15, 15f): "Resting a moment" — mirrors
              call-status-cluster.tsx's fifth glyph exactly (no dot/icon,
              no anti-strobe delay). */}
          <div
            className={cn(
              'col-start-1 row-start-1 flex items-center transition-opacity duration-200',
              isResting ? 'opacity-[1]' : 'opacity-[0]'
            )}
          >
            <span
              className="truncate text-[13px] font-medium"
              style={{ color: T13_STATUS_COLORS.restingLabel }}
            >
              Resting a moment
            </span>
          </div>

          {/* [FB34/Turn 16] connecting/failed override — mirrors
              call-status-cluster.tsx's sixth glyph, at the bar's mini dot
              scale (4px vs desktop's 5px). */}
          <div
            className={cn(
              'col-start-1 row-start-1 flex items-center gap-[8px] transition-opacity duration-200',
              hasOverride ? 'opacity-[1]' : 'opacity-[0]',
              statusOverrideDots &&
                'motion-safe:animate-call-rise motion-safe:[animation-delay:90ms]'
            )}
          >
            {statusOverrideDots && (
              <div
                className="flex shrink-0 items-center gap-[4px]"
                aria-hidden="true"
              >
                <span
                  className="size-[4px] rounded-full opacity-[.25] motion-safe:animate-call-dot-blink motion-reduce:opacity-100"
                  style={{ backgroundColor: T13_STATUS_COLORS.connectingDot }}
                />
                <span
                  className="size-[4px] rounded-full opacity-[.25] motion-safe:animate-call-dot-blink motion-reduce:opacity-100"
                  style={{
                    backgroundColor: T13_STATUS_COLORS.connectingDot,
                    animationDelay: '.24s',
                  }}
                />
                <span
                  className="size-[4px] rounded-full opacity-[.25] motion-safe:animate-call-dot-blink motion-reduce:opacity-100"
                  style={{
                    backgroundColor: T13_STATUS_COLORS.connectingDot,
                    animationDelay: '.48s',
                  }}
                />
              </div>
            )}
            <span
              className={cn(
                'truncate',
                statusOverrideDots
                  ? 'text-[13px] font-semibold'
                  : 'text-[13px] font-medium'
              )}
              style={{
                color:
                  statusOverrideDots || statusOverrideWarm
                    ? T13_STATUS_COLORS.connectingLabel
                    : T13_STATUS_COLORS.idleLabel,
              }}
            >
              {statusOverride}
            </span>
          </div>
        </div>
      </div>

      {/* The Live pill (children) shares the content entrance — the orb
          slot above deliberately does NOT (it's the FLIP target). */}
      <div className={cn(entranceCn)}>{children}</div>
    </div>
  );
}
